import clsx from 'clsx'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import type { ComponentProps, ElementType, JSX, ReactNode, RefAttributes } from 'react'
import styled from 'styled-components'
import BodyText from '../Typography/BodyText'
import Caption from '../Typography/Caption'

export const _ListItemElement = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.S};
  align-items: center;
  gap: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  transition: background-color 200ms, color 200ms;
  padding-inline: ${rem(16)};

  &.size-small { padding-block: ${rem(4)}; }
  &.size-medium { padding-block: ${rem(8)}; }
  &.size-large { padding-block: ${rem(12)}; }
`

const ItemContent = styled(Group)`
  overflow: hidden;
  flex-grow: 1;
`

const CenterContent = styled.div`
  width: 100%;
`

interface Props extends RefAttributes<HTMLElement> {
  /** An icon element that will go at the start (often the 'left' side) of the List item. */
  startIcon?: ReactNode
  /** An icon element that will go at the end (often the 'right' side) of the List item. */
  endIcon?: ReactNode
  /**
   * Sets the text above the title.
   */
  overline?: ReactNode
  /**
   * Allows applying format to the overline text.
   *
   * @default uppercase
   */
  overlineFormat?: ComponentProps<typeof Caption>['format']
  /**
   * Sets the main label.
   *
   * This is the most prominent text.
   **/
  title: ReactNode
  /** Allows applying format to the title text. */
  titleFormat?: ComponentProps<typeof BodyText>['format']
  titleWeight?: ComponentProps<typeof BodyText>['weight']
  /**
   * @default 2
   */
  titleLineClamp?: ComponentProps<typeof BodyText>['lineClamp']
  titleColour?: App.Design.ContentColour
  /**
   * Sets the text beneath the title.
   *
   * This is for less important text, and is visually less prominent.
   **/
  subtitle?: ReactNode
  /** Allows applying format to the subtitle text. */
  subtitleFormat?: ComponentProps<typeof Caption>['format']
  subtitleLineClamp?: ComponentProps<typeof Caption>['lineClamp']
  className?: string
  /**
   * Determines the height/padding of the item, the text within it
   * as well as the size of icons if they are provided.
   *
   * @default large
   **/
  size?: 'small' | 'medium' | 'large'
  /**
   * Children will be placed in the 'slot' specified in Figma.
   */
  children?: ReactNode
  disabled?: boolean
  as?: ElementType | keyof JSX.IntrinsicElements
}

function _ListItem({
  startIcon,
  endIcon,
  overline,
  overlineFormat = 'uppercase',
  title,
  titleFormat,
  titleLineClamp = 2,
  titleColour,
  subtitle,
  subtitleFormat,
  subtitleLineClamp,
  className,
  size = 'large',
  children,
  disabled,
  titleWeight,
  ...rest
}: Props) {
  const captionSize = size === 'large' ? 'large' : 'medium'

  return <_ListItemElement
    {...rest}
    className={clsx(className, `size-${size}`)}
  >
    {startIcon}
    <ItemContent direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
      <CenterContent>
        {overline && <Caption variant="small" weight="bold" format={overlineFormat}>
          {overline}
        </Caption>}
        <BodyText
          variant={size}
          lineClamp={titleLineClamp}
          format={titleFormat}
          colour={titleColour}
          weight={titleWeight}
        >
          {title}
        </BodyText>
        {subtitle && <Caption
          variant={captionSize}
          format={subtitleFormat}
          lineClamp={subtitleLineClamp}
          colour="light-foreground-alt"
        >
          {subtitle}
        </Caption>}
      </CenterContent>
      {children}
    </ItemContent>
    {endIcon}
  </_ListItemElement>
}

export default _ListItem
